import { useState, useEffect, useRef, useCallback } from 'react';
import { chatPageService } from '../../../services/index.js';
import { userModelGroupMappingService } from '../../../services/index.js';

export const useDashboard = (activeUseCase, initialTokenUsage = 0, initialConversationId = null, historyModelGroupId = null) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [tokenUsage, setTokenUsage] = useState(initialTokenUsage);
  const [models, setModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState(null);
  const [isModelDropdownOpen, setIsModelDropdownOpen] = useState(false);
  const [chatHistory, setChatHistory] = useState([]);
  const [conversationId, setConversationId] = useState(initialConversationId);
  const messagesEndRef = useRef(null);
  const isSendingRef = useRef(false);
  const messageIdRef = useRef(Date.now());

  const getNextMessageId = () => {
    messageIdRef.current += 1;
    return messageIdRef.current;
  };

  const fetchChatHistory = async () => {
    try {
      const history = await chatPageService.fetchChatHistory();
      setChatHistory(history.data);
    } catch (error) {
      console.log('Fetch chat history error:', error);
    }
  };

  useEffect(() => {
    fetchChatHistory();
  }, []);

  useEffect(() => {
    const userId = localStorage.getItem('userId');
    if (!userId) return;
    userModelGroupMappingService
      .getUserModelGroupMappingsByUser({ userId: Number(userId) })
      .then(res => {
        const data = (Array.isArray(res) ? res
          : Array.isArray(res?.data) ? res.data
          : Array.isArray(res?.data?.data) ? res.data.data
          : []).map(m => ({
            modelGroupId: m.modelGroupId || m.ModelGroupId,
            modelGroupName: m.modelGroupName || m.ModelGroupName || m.name || m.Name || '',
          }));
        setModels(data);
      })
      .catch(err => console.error('ModelGroup fetch error:', err));
  }, []);

  useEffect(() => {
    if (!models.length) return;
    console.log('[useDashboard] matching effect - historyModelGroupId:', historyModelGroupId, 'models:', models);
    const match = historyModelGroupId
      ? models.find(m => String(m.modelGroupId) === String(historyModelGroupId))
      : null;
    console.log('[useDashboard] match result:', match);
    setSelectedModel(match || models[0] || null);
  }, [models, historyModelGroupId]);

  useEffect(() => {
    if (activeUseCase && activeUseCase.id !== 'loaded') {
      setMessages([]);
      // const startNewChat = async () => {
      //   try {
      //     const response = await chatPageService.startNewChat(activeUseCase.useCaseId);
      //     await fetchChatHistory();
      //   } catch (error) {
      //     console.log('Start new chat error:', error);
      //   }
      // };
      // startNewChat();
      setConversationId(0);
      setTokenUsage(0);
      sessionStorage.removeItem('activeModelGroupId');
    }
  }, [activeUseCase]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  // const togglePin = (id, e) => {
  //   e.stopPropagation();
  //   setChatHistory(prev => prev.map(chat => chat.id === id ? { ...chat, pinned: !chat.pinned } : chat));
  // };

  const parseHistoryMessages = useCallback((rawMessages) =>
    rawMessages
      .filter(msg => msg.senderType !== 'system')
      .map(msg => {
        let table = null;
        let chartSpec = null;
        let chartData = null;
        let sources = [];
        let downloads = [];
        if (msg.messageJSON) {
          try {
            const parsed = JSON.parse(msg.messageJSON);
            console.log('Parsed messageJSON:', parsed);
            if (parsed.Table || parsed.table) {
              table = {
                title: parsed.Table?.Title || parsed.table?.title || parsed.Table?.title || parsed.table?.Title || '',
                columns: parsed.Table?.Columns || parsed.table?.columns || parsed.Table?.Columns || parsed.table?.Columns || [],
                rows: parsed.Table?.Rows || parsed.table?.rows || parsed.Table?.Rows || parsed.table?.Rows || [],
              };
            }
            if (parsed.Chart || parsed.chart) {
              chartSpec = parsed.Chart?.Spec || parsed.Chart?.spec || parsed.chart?.Spec || parsed.chart?.spec || null;
              const rawData = parsed.Chart?.Data || parsed.Chart?.data || parsed.chart?.Data || parsed.chart?.data || null;
              if (rawData) {
                chartData = {
                  columns: rawData.Columns || rawData.columns || [],
                  rows: rawData.Rows || rawData.rows || [],
                };
              }
            }
            if (parsed.Sources || parsed.sources) {
              sources = Array.isArray(parsed.Sources || parsed.sources) ? (parsed.Sources || parsed.sources) : [(parsed.Sources || parsed.sources)];
            }
            if (parsed.Downloads || parsed.downloads) {
              downloads = Array.isArray(parsed.Downloads || parsed.downloads) ? (parsed.Downloads || parsed.downloads) : [(parsed.Downloads || parsed.downloads)];
            }
            if (typeof parsed.messageText === 'object' ||  typeof parsed.MessageText === 'object' || typeof parsed.message === 'object' || typeof parsed.Message === 'object') {
              parsed.messageText = JSON.stringify(parsed.messageText || parsed.MessageText || parsed.message || parsed.Message);
            }
            if (parsed.senderType === 'assistant'  || parsed.senderType === 'bot' || parsed.SenderType === 'bot' || parsed.SenderType === 'assistant') {
              parsed.senderType = 'bot';
            }
            if (parsed.conversationId || parsed.ConversationId) {
              parsed.conversationId = Number(parsed.conversationId || parsed.ConversationId);
            }
            if (parsed.tokensUsed || parsed.TokensUsed) {
              parsed.tokensUsed = Number(parsed.tokensUsed || parsed.TokensUsed);
            }
            if (parsed.meta || parsed.Meta) {
              const tokens =
                Number(parsed?.meta?.tokensUsed?.totalTokens) ||
                Number(parsed?.meta?.tokensUsed?.outputTokens) ||
                Number(parsed?.Meta?.TokensUsed?.OutputTokens) ||
                Number(parsed?.Meta?.TokensUsed?.TotalTokens) || 0;    
                parsed.tokensUsed = tokens;
            }
          } catch (e) {
            console.log('Error parsing messageJSON:', e);
          }
        }
        return {
          ...msg,
          messageText: typeof msg.messageText === 'object' ||  typeof msg.MessageText === 'object' || typeof msg.message === 'object' || typeof msg.Message === 'object' ? JSON.stringify(msg.messageText) : (msg.messageText || ''),
          senderType: msg.senderType === 'assistant'  || msg.senderType === 'bot' || msg.SenderType === 'bot' || msg.SenderType === 'assistant' ? 'bot' : msg.senderType,
          table,
          chartSpec,
          chartData,
          sources,
          downloads
        };
      }), []);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || isSendingRef.current) return;

    const userText = input.trim();
    isSendingRef.current = true;
    setMessages(prev => [...prev, { messageId: getNextMessageId(), senderType: 'user', messageText: userText }]);
    setInput('');
    setIsTyping(true);
    setTokenUsage(prev => prev + Math.max(1, Math.ceil(userText.length / 4)));

    try {
      const botResponse = await chatPageService.sendMessage(userText, activeUseCase.userUseCaseId, conversationId, selectedModel?.modelGroupId ?? null);
      if (botResponse?.conversationId || botResponse?.ConversationId && Number(botResponse.conversationId || botResponse.ConversationId) !== Number(conversationId || 0)) {
        setConversationId(Number(botResponse.conversationId || botResponse.ConversationId));
      }
      setMessages(prev => [
        ...prev,
        {
          messageId: getNextMessageId(),
          senderType: 'bot',
          messageText: botResponse?.messageText || botResponse?.Message || botResponse?.message || 'No response returned.',
          table: botResponse?.table || botResponse?.Table || null,
          chartSpec: botResponse?.chart?.spec || botResponse?.Chart?.Spec || null,
          chartData: botResponse?.chart?.data || botResponse?.Chart?.Data || null,
          sources: botResponse?.sources || botResponse?.Sources || [],
          downloads: botResponse?.downloads || botResponse?.Downloads || [],
          mediaUrl: botResponse?.mediaUrl || botResponse?.MediaUrl || null,
        },
      ]);
      setTokenUsage(prev => prev + Number(botResponse?.tokensUsed || botResponse?.TokensUsed || 0));
      fetchChatHistory();
    } catch (error) {
      console.log('Send message error:', error);
      setMessages(prev => [...prev, { messageId: getNextMessageId(), senderType: 'bot', messageText: 'Sorry, there was an error processing your request.' }]);
    } finally {
      isSendingRef.current = false;
      setIsTyping(false);
    }
  };

  const handleTryAgain = useCallback((msgId) => {
    setIsTyping(true);
    setMessages(prev => prev.map(msg => msg.messageId === msgId ? { ...msg, messageText: "Regenerating response..." } : msg));
    setTimeout(() => {
      const useCaseTitle = activeUseCase?.title || activeUseCase?.useCaseName || 'this workspace';
      const refreshedResponse = `Here is a refined response regarding ${useCaseTitle}.\n\nI have re-evaluated the context and found additional insights that align closely with our standard operating procedures. Can I elaborate further?`;
      setMessages(prev => prev.map(msg => msg.messageId === msgId ? { ...msg, messageText: refreshedResponse } : msg));
      setIsTyping(false);
    }, 1500);
  }, [activeUseCase]);

  const getChatMessages = async (id) => {
    console.log('Fetching messages for conversation ID:', id);
    try {
      const chatMessages = await chatPageService.getChatMessages(id);
      const formattedMessages = parseHistoryMessages(chatMessages);
      setMessages(formattedMessages);
    } catch (error) {
      console.log('Get chat messages error:', error);
    }
  };

  return {
    messages, input, setInput, isTyping, copiedId, setCopiedId, tokenUsage,
    models, selectedModel, setSelectedModel, isModelDropdownOpen, setIsModelDropdownOpen,
    chatHistory, handleSendMessage, handleTryAgain, messagesEndRef, getChatMessages, conversationId, setConversationId, setMessages, parseHistoryMessages, fetchChatHistory
  };
};
